/**
 * SalesAccountDetail — the account page: GET /v1/sales/accounts/{id} (A1) in
 * ONE read — the account, its contacts, its deals split open/closed, the
 * activities logged against those deals or contacts (newest first, the server
 * caps at 100), the issued invoices behind its won deals, and since Account
 * detail v3 AD1 (design note §45) the server's SUMMARY: the hero chips and the
 * KPI strip, computed over every row. The page never sums a figure itself; an
 * api older than AD1 sends no summary and the page then shows no KPI strip.
 *
 * Layout (spec v3 §2, AD1's share): breadcrumb Sales › Accounts › name with
 * "Account n of N · by open value", stepped with ‹ › or J / K; the hero —
 * tile, name, chips (Prospect / Customer; Never contacted / First call
 * logged), the meta line exactly as the spec (domain with the stored value as
 * a hint, region, sector, Owner, phone) and the actions Edit, + Contact,
 * + Deal, Log activity; the five KPI tiles; then Deals and the one Timeline
 * on the left, People, Company facts and Invoices on the right. The account
 * plan, the first call brief, Heat and the engagement strip are later slices
 * (AD5, AD7) and render nothing here. The spec's "same time zone" has no
 * source on the read path and is not shown.
 *
 * AD3 (slice plan v2, ruled 2026-10-31): ✦ Account health heads the left
 * column — the SERVER's six checks (AccountHealthCard); the open deal rows
 * carry their rule-set-4 chips from rules[]; the deal the close-date check
 * names gets the ✦ Close date check with "Move to <date>" (records only),
 * which sends the existing deal PUT with expectedClose alone.
 *
 * Owner (§45 ruling 4): the meta line reads "Owner" with the name of the
 * owner of the account's largest open deal (server-derived, no column); "from
 * the largest open deal" is said only in its tooltip. AD3 ruling D3: test
 * deals are left out exactly as the health card's Owner check leaves them out
 * (the server's one pick), so with only test deals open the tooltip reads the
 * check's own state — no open deal to take an owner from.
 *
 * Header edits are inline (P1-1: name / website / phone / notes; Accounts S4:
 * region and sector) through PATCH /v1/sales/accounts/{id} — every header
 * field is sent, so a cleared input clears the column. The mutation response
 * overrides the fetched account (keyed override, never copied into state from
 * the fetch). Every write control is records-gated (lib/writeAccess, fail
 * closed); Log activity is the ONE sheet (useLogActivity, null without access).
 *
 * Money (§43 ruling 3, AD0): pipeline and deal figures are compact; invoice
 * totals stay exact — they are issued amounts.
 *
 * Accounts S6: the survivor's "Merged into this account" card lists every
 * merge into it that is not undone, each with "Undo merge" (owner or admin
 * only — canMerge, from /v1/me, fail closed); a failed read hides the card.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Globe, Phone, Pencil, MapPin, Briefcase, GitMerge, ChevronLeft, ChevronRight, Plus, UserRound } from 'lucide-react'
import { Avatar, LoadingState, ErrorState } from '../../DashboardShared'
import type { SalesAccountView, SalesDeal } from '../../types'
import type { SalesAccountHealthCheck, SalesAccountSeat, SalesAccountWaitingLead } from '../../../../api/types'
import { useAsync, fetchAccount, fetchAccountMerges, fetchAccounts, updateAccount, updateDeal, linkDealContact, setDealContactRole, attachAccountLead, relAgo, fmtDate, cachedMeIdentity } from '../../api'
import { money, compactMoney } from '../../../../lib/money'
import { apiErrorMessage } from '../../../../api/client'
import { toast } from 'sonner'
import { accent, ink, surface } from '../../../../theme/tokens'
import { AccountCodeSelect } from './AccountCodeSelect'
import { regionLabel, sectorLabel } from '../../../../lib/accountCodes'
import { codedValue, headcountText } from './auditStrip'
import { movedPhrase, undoMerge } from './accountMergeActions'
import { websiteDomain } from './accountPopup'
import { useWriteAccess } from '../../../../lib/writeAccess'
import { useLogActivity } from './logActivityShared'
import { shortcutEligible } from '../../../../lib/keyboardGuard'
import { shortcutsPrefKey, readShortcutsEnabled } from '../../../../lib/shortcutsPref'
import { stageDotFor } from '../../stageDotFor'
import { laneLabel } from './pipelineView'
import { formatDay } from './dealRail'
import { closeDay } from './dealHealthCard'
import { londonDay } from './workingDays'
import { NewDealDrawer } from './SalesPipeline'
import { NewContactDrawer } from './SalesContacts'
import { AccountTimeline } from './AccountTimeline'
import { AccountHealthCard } from './AccountHealthCard'
import { CompaniesHouseCard, emptyCompaniesHouseMemory } from './CompaniesHouseCard'
import { statusWithReadTime } from './companiesHouseView'
import { waitingLeadLine, attachTitle, attachedToast } from './waitingLeads'
import { dealRuleChips, ruleTone, closeCheckSentence, shortDay, coverageSeats, peopleHead, seatHint, seatSearchUrl, SEAT_INFO, type RuleChip } from './accountHealthView'
import {
  STEP_CAP, stepOrder, stepPosition, relationshipChip, contactChip, accountAgeDays, closeRelative, ageLine,
} from './accountDetailView'

/** A website stored without a scheme still needs one to be a link. */
function hrefFor(website: string): string {
  return /^https?:\/\//i.test(website) ? website : `https://${website}`
}

/** A deal's stage in the app's own words (§43 ruling 2: New lead, Qualified,
 *  Proposal sent, Negotiation), in the stage-badge capsule. */
function StageChip({ stage }: { stage: SalesDeal['stage'] }) {
  return (
    <span className="stage-badge" style={{ background: surface.panel, color: ink.body }}>
      <span className="stage-badge-dot" style={{ background: stageDotFor(stage) }} aria-hidden="true" />
      {laneLabel(stage)}
    </span>
  )
}

/** AD3 (Q6): one rule-set-4 chip, coloured by its severity only. */
function RuleChipBadge({ chip, title }: { chip: RuleChip; title?: string }) {
  const tone = ruleTone(chip.severity)
  return (
    <span className={tone ? 'k-badge' : 'k-badge-outline text-ink-muted'} style={tone ?? undefined} title={title ?? chip.text}
      data-code={chip.code} data-severity={chip.severity}>
      <span className="k-badge-dot" />{chip.text}
    </span>
  )
}

/** AD3: an open deal's chips from the server's rules[] — at most three, then
 *  "+n" whose title names the rest. */
function DealChips({ deal }: { deal: SalesDeal }) {
  const { shown, more } = dealRuleChips(deal.rules, deal.isTest)
  if (shown.length === 0) return null
  return (
    <div className="flex items-center gap-1.5 flex-wrap mt-1.5" aria-label={`Risks on ${deal.name}`}>
      {shown.map(c => <RuleChipBadge key={c.code} chip={c} />)}
      {more.length > 0 && (
        <span className="k-badge-outline text-ink-muted" title={more.map(c => c.text).join(' · ')}>+{more.length}</span>
      )}
    </div>
  )
}

/** AD3 (spec §4): the ✦ Close date check under the deal it names, with the
 *  one write — "Move to <date>" — for records-level users only. */
function CloseDateCheck({ check, today, onMove }: {
  check: SalesAccountHealthCheck
  today: string
  onMove?: () => Promise<void>
}) {
  const [moving, setMoving] = useState(false)
  const day = shortDay(check.params.suggestedClose, today)
  return (
    <div className="rounded-control bg-surface-panel px-3 py-2.5 my-2 flex items-start justify-between gap-3 flex-wrap" aria-label="Close date check">
      <div className="text-12 text-ink-secondary min-w-0 max-w-xl">
        <span className="font-semibold text-ink-primary">✦ Close date check · </span>{closeCheckSentence(check, today)}
      </div>
      {onMove && check.params.suggestedClose && (
        <button type="button" className="k-btn-secondary flex-shrink-0" disabled={moving}
          onClick={async () => { setMoving(true); try { await onMove() } finally { setMoving(false) } }}>
          {moving ? 'Moving…' : `Move to ${day}`}
        </button>
      )}
    </div>
  )
}

function DealRow({ deal, onOpen }: { deal: SalesDeal; onOpen: (id: string) => void }) {
  const closes = deal.closedDate ? `closed ${deal.closedDate}` : deal.expectedClose ? `closes ${deal.closeDate}` : 'no close date'
  return (
    <button type="button" onClick={() => onOpen(deal.id)}
      className="w-full text-left flex items-center justify-between gap-3 py-3 border-b border-edge-row last:border-0 hover:bg-surface-row-hover transition-colors">
      <div className="min-w-0">
        <div className="text-13 font-semibold text-ink-primary truncate">{deal.name}</div>
        <div className="text-11 text-ink-muted mt-0.5">
          {deal.owner !== '—' ? deal.owner : 'Unassigned'} · updated {deal.lastActivity} · {closes}
        </div>
        {!deal.closedDate && <DealChips deal={deal} />}
      </div>
      <div className="flex items-center gap-3 flex-shrink-0">
        <div className="text-right">
          <div className="text-13 font-medium text-ink-primary">{compactMoney(deal.amount, deal.currency)}</div>
          {deal.weightedPence !== undefined && !deal.closedDate && (
            <div className="text-11 text-ink-muted">{compactMoney(deal.weightedPence, 'GBP')} weighted</div>
          )}
        </div>
        <StageChip stage={deal.stage} />
      </div>
    </button>
  )
}

/** Accounts S4: a region or sector on the meta line — its label, or "Not
 *  set" with an Add that opens the edit form on that control. */
function CodedField({ icon, name, label, onAdd }: { icon: React.ReactNode; name: string; label: string; onAdd?: () => void }) {
  if (label) {
    return <span className="inline-flex items-center gap-1" aria-label={`${name}: ${label}`}><span className="text-ink-faint">{icon}</span>{label}</span>
  }
  return (
    <span className="inline-flex items-center gap-1 text-ink-muted">
      {icon}{name}: Not set
      {onAdd && <button type="button" className="k-accounts-add" aria-label={`Add ${name.toLowerCase()}`} onClick={onAdd}>Add</button>}
    </span>
  )
}

/** One KPI tile (spec §2.3): a label, the figure, one line of context. */
function Kpi({ label, value, sub }: { label: string; value: string; sub?: string | null }) {
  return (
    <div className="k-card px-4 py-3 min-w-0">
      <div className="text-overline text-ink-muted uppercase tracking-wider">{label}</div>
      <div className="text-section-title font-bold text-ink-primary mt-1 truncate">{value}</div>
      {sub && <div className="text-11 text-ink-muted mt-0.5 truncate" title={sub}>{sub}</div>}
    </div>
  )
}

/** One Company facts row (spec §9): the value with its code as the secondary
 *  line, or "Not set", a reason and Add. */
function FactRow({ name, value, secondary, reason, onAdd, multiline = false }: {
  name: string; value: string; secondary?: string | null; reason?: string; onAdd?: () => void
  /** AD2: the billing address keeps its line breaks. */
  multiline?: boolean
}) {
  // The meta line's S4 Adds are named "Add region" / "Add sector"; these say
  // which card they belong to, so the two never share a name.
  return (
    <div className="flex items-start justify-between gap-3 py-2.5 border-b border-edge-row last:border-0">
      <div className="text-12 text-ink-muted flex-shrink-0 w-24">{name}</div>
      <div className="min-w-0 flex-1 text-right">
        {value ? (
          <>
            <div className={`text-13 text-ink-primary ${multiline ? 'whitespace-pre-line break-words' : 'truncate'}`}>{value}</div>
            {secondary && <div className="text-11 text-ink-muted truncate">{secondary}</div>}
          </>
        ) : (
          <>
            <div className="text-13 text-ink-muted">
              Not set{onAdd && <> <button type="button" className="k-accounts-add" aria-label={`Add ${name.toLowerCase()} in company facts`} onClick={onAdd}>Add</button></>}
            </div>
            {reason && <div className="text-11 text-ink-muted">{reason}</div>}
          </>
        )}
      </div>
    </div>
  )
}

interface HeaderDraft {
  name: string; website: string; phone: string; notes: string; region: string; sector: string
  /** AD2 (0146): the typed-in facts; headcount as typed ('' = not set). */
  headOffice: string; headcount: string; legalEntity: string; billingAddress: string; vatNumber: string
}

/** Which control the edit form opens on (each Add focuses its own). */
type EditFocus = 'name' | 'website' | 'phone' | 'region' | 'sector' | 'headOffice' | 'headcount' | 'billing'

const EMPTY_DRAFT: HeaderDraft = {
  name: '', website: '', phone: '', notes: '', region: '', sector: '',
  headOffice: '', headcount: '', legalEntity: '', billingAddress: '', vatNumber: '',
}

function draftFrom(a: SalesAccountView): HeaderDraft {
  return {
    name: a.name, website: a.website, phone: a.phone, notes: a.notes, region: a.region ?? '', sector: a.sector ?? '',
    headOffice: a.headOffice ?? '', headcount: a.headcount ? String(a.headcount) : '',
    legalEntity: a.legalEntity ?? '', billingAddress: a.billingAddress ?? '', vatNumber: a.vatNumber ?? '',
  }
}

/** AD2: the Size field as typed — '' clears (null); otherwise a whole number
 *  above 0, or undefined when it is not one (the form refuses to save). */
function parseHeadcount(text: string): number | null | undefined {
  const t = text.trim().replace(/,/g, '')
  if (t === '') return null
  if (!/^\d+$/.test(t)) return undefined
  const n = Number(t)
  return Number.isSafeInteger(n) && n > 0 && n <= 2_147_483_647 ? n : undefined
}

export function SalesAccountDetail({ accountId, onBack, onSelectDeal, onNavigate, onStep, initialEdit = false, canMerge = false }: {
  accountId: string
  onBack: () => void
  onSelectDeal: (dealId: string) => void
  onNavigate?: (dest: string) => void
  /** AD1: step to another account (‹ › and J / K); absent = no stepper. */
  onStep?: (accountId: string) => void
  /** Accounts S3: arrive with the header edit form open (the list's ⋯ Edit). */
  initialEdit?: boolean
  /** S6 ruling 1: owner or admin per /v1/me; false hides the Undo merge card. */
  canMerge?: boolean
}) {
  // S6: an Undo moves rows back, and AD1's creates add rows — every read
  // re-runs after one (the nonce).
  const [nonce, setNonce] = useState(0)
  const q = useAsync(() => fetchAccount(accountId), [accountId, nonce])
  const merges = useAsync(() => (canMerge ? fetchAccountMerges(accountId).catch(() => null) : Promise.resolve(null)), [accountId, nonce, canMerge])
  // AD1: the stepper's order — the accounts list read (server figures), a
  // failure hides the stepper rather than guessing a position.
  const list = useAsync(() => (onStep ? fetchAccounts(undefined, STEP_CAP).catch(() => null) : Promise.resolve(null)), [onStep !== undefined])
  const [undoing, setUndoing] = useState<string | null>(null)
  const [override, setOverride] = useState<{ id: string; account: SalesAccountView } | null>(null)
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<HeaderDraft>(EMPTY_DRAFT)
  const [focus, setFocus] = useState<EditFocus>('name')
  const [saving, setSaving] = useState(false)
  const [pendingEdit, setPendingEdit] = useState(initialEdit)
  const [creating, setCreating] = useState<'contact' | 'deal' | null>(null)
  // AD4: the people seat a "+ Contact" was opened for (the new contact is
  // linked to the seat's deal with the seat's role once it is saved).
  const [seatFor, setSeatFor] = useState<SalesAccountSeat | null>(null)
  // AD6: the waiting lead an Attach is in flight for (its button is disabled).
  const [attachingId, setAttachingId] = useState<string | null>(null)
  // CH1 (Q5): the Companies House card opens only when a records user asks.
  const [chOpen, setChOpen] = useState(false)
  const chMemory = useRef(emptyCompaniesHouseMemory())
  // Stepping to another account (J / K) keeps this page mounted: the card and
  // what it remembers belong to the account it was opened on.
  useEffect(() => { chMemory.current = emptyCompaniesHouseMemory(); setChOpen(false) }, [accountId])
  // The clock, read ONCE per mount (a render must not call it).
  const [today] = useState(() => londonDay(new Date()))
  // The header edit, + Contact and + Deal are records-level writes
  // (lib/writeAccess; fail closed). Log activity is null without access.
  const { records } = useWriteAccess()
  const logActivity = useLogActivity()

  // A logged activity changes the timeline and the touch figures.
  useEffect(() => logActivity?.subscribe(() => setNonce(n => n + 1)), [logActivity])

  // J / K step through the accounts (spec §2.1) — the L shortcut's switch and
  // guard (WCAG 2.1.4: the device-local switch turns every single key off).
  const stepRef = useRef<{ prev: string | null; next: string | null }>({ prev: null, next: null })
  useEffect(() => {
    if (!onStep) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (!readShortcutsEnabled(shortcutsPrefKey(cachedMeIdentity()))) return
      if (e.key === 'j' || e.key === 'J') {
        if (!shortcutEligible(e, 'j') || !stepRef.current.next) return
        e.preventDefault()
        onStep(stepRef.current.next)
      } else if (e.key === 'k' || e.key === 'K') {
        if (!shortcutEligible(e, 'k') || !stepRef.current.prev) return
        e.preventDefault()
        onStep(stepRef.current.prev)
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onStep])

  const rows = list.data ?? null
  const position = rows && rows.length < STEP_CAP
    ? stepPosition(stepOrder(rows.map(r => ({ id: r.id, name: r.name, openValueBasePence: r.openValuePence }))), accountId)
    : null
  const prevId = position?.prev ?? null
  const nextId = position?.next ?? null
  // The J / K handler reads this ref, so it is written in a LAYOUT effect: React runs it
  // synchronously in the same commit that puts the counter (and the ‹ › targets) in the
  // DOM, so a key pressed the moment the counter shows can never find the previous
  // render's targets. A passive effect ran later, and a J in that gap was dropped (the
  // AD1 stepper test flaked 3 in 50 on that gap).
  useLayoutEffect(() => { stepRef.current = { prev: prevId, next: nextId } }, [prevId, nextId])

  if (q.loading) return <LoadingState />
  if (q.error || !q.data) return <ErrorState message={q.error ?? 'Account not found'} />

  const view = q.data
  const account = override && override.id === accountId ? override.account : view.account
  const summary = view.summary

  // The ⋯ Edit arrival: open the form once, from the loaded account (state
  // adjusted during render — React re-renders at once, before painting).
  if (pendingEdit && records) {
    setPendingEdit(false)
    setDraft(draftFrom(account))
    setFocus('name')
    setEditing(true)
  }

  // AD0 (§45): the website reads as its domain — the same normalisation as the
  // server's generated website_domain (0140) — with the stored value as a hint
  // when they differ. Display only: nothing is re-saved (S4's ruling).
  const shownDomain = account.website ? (websiteDomain(account.website) || account.website.trim()) : ''
  const ageDays = accountAgeDays(account.createdAt, today)
  const sectorCoded = account.sector ? codedValue('sector', account.sector) : null
  const regionCoded = account.region ? codedValue('region', account.region) : null
  const billingMissing = !account.legalEntity || !account.billingAddress || !account.vatNumber

  function startEdit(on: EditFocus = 'name') {
    setDraft(draftFrom(account))
    setFocus(on)
    setEditing(true)
  }

  async function save() {
    if (!draft.name.trim()) { toast.error('Account name is required'); return }
    const headcount = parseHeadcount(draft.headcount)
    if (headcount === undefined) { toast.error('Size must be a whole number of people above 0'); return }
    setSaving(true)
    try {
      const next = await updateAccount(accountId, {
        name: draft.name.trim(),
        website: draft.website.trim(),
        phone: draft.phone.trim(),
        notes: draft.notes.trim(),
        region: draft.region,
        sector: draft.sector,
        headOffice: draft.headOffice.trim(),
        headcount,
        legalEntity: draft.legalEntity.trim(),
        billingAddress: draft.billingAddress.trim(),
        vatNumber: draft.vatNumber.trim(),
      })
      setOverride({ id: accountId, account: next })
      setEditing(false)
      toast.success('Account updated')
      setNonce(n => n + 1)
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to update account'))
    } finally {
      setSaving(false)
    }
  }

  // AD3 (Q4): "Move to <date>" sends the existing deal update with the
  // server's suggested close and nothing else; the deal's field history is
  // the audit (actor = the clicking user).
  const closeCheck = view.health?.checks?.find(c => c.id === 'close_date' && c.state === 'fail')
  // AD3 ruling D3: the hero Owner and the health's Owner check share the
  // server's deal pick (largest open NON-test deal); "no open deal" is that
  // check's not_applicable, never the raw open count, which counts test deals.
  const ownerCheck = view.health?.checks?.find(c => c.id === 'owner')
  // AD4 (spec §8; design note §45 ruling 1, Q9, Privacy): the deal's people
  // seats from the server's coverage check; a filled seat names its contact,
  // an empty one is a dashed row with Find (an external search in the user's
  // own browser — Klaxiora fetches and stores nothing) and "+ Contact".
  const coverageCheck = view.health?.checks?.find(c => c.id === 'people_coverage')
  const seats = coverageSeats(coverageCheck)
  const seatDomain = view.health?.checks?.find(c => c.id === 'website')?.params?.domain
  const seatOfContact = new Map(seats.filter(x => x.filled && x.contactId).map(x => [x.contactId as string, SEAT_INFO[x.seat].label]))
  async function fillSeat(seat: SalesAccountSeat, contactId: string, contactName: string) {
    const dealId = coverageCheck?.params?.dealId
    const dealName = coverageCheck?.params?.dealName ?? 'the deal'
    const label = SEAT_INFO[seat.seat].label
    if (!dealId) return
    try {
      await linkDealContact(dealId, contactId)
      await setDealContactRole(dealId, contactId, seat.role)
      toast.success(`${contactName} added to ${dealName} as ${label}`)
    } catch (e) {
      // The contact exists either way: lead with that, then the server's reason.
      toast.error(`${contactName} was saved, but adding them to ${dealName} as ${label} failed: ${apiErrorMessage(e, 'no reason was given')}`)
    } finally {
      setNonce(n => n + 1)
    }
  }
  // AD6 (spec §7 item 2, §8; design note §45 ruling 5): leads waiting in
  // Inbound for this account. "Attach to this account" adds the lead to the
  // deal the People card is about (the coverage check's deal) through the one
  // owner/admin route — linked to the account when unlinked, the Inbound lead
  // closed, no deal created. Without an open deal there is nothing to attach to.
  const waiting = view.waitingLeads ?? []
  const waitingOf = new Map(waiting.map(l => [l.contactId, l]))
  const attachDealId = coverageCheck?.params?.dealId
  const attachDealName = coverageCheck?.params?.dealName ?? 'the deal'
  async function attachLead(lead: SalesAccountWaitingLead) {
    if (!attachDealId) return
    setAttachingId(lead.contactId)
    try {
      await attachAccountLead(accountId, lead.contactId, attachDealId)
      toast.success(attachedToast(lead.fullName, attachDealName))
    } catch (e) {
      toast.error(apiErrorMessage(e, `Could not attach ${lead.fullName}`))
    } finally {
      setAttachingId(null)
      setNonce(n => n + 1)
    }
  }
  function waitingParts(lead: SalesAccountWaitingLead | undefined) {
    if (!lead) return { line: null, action: null }
    const line = waitingLeadLine(lead.createdAt, today)
    const action = records && attachDealId ? (
      <button type="button" className="k-btn-secondary flex-shrink-0 disabled:opacity-40" disabled={attachingId === lead.contactId}
        title={attachTitle(lead, attachDealName)} aria-label={`Attach ${lead.fullName} to this account`}
        onClick={() => { void attachLead(lead) }}>Attach to this account</button>
    ) : null
    return { line, action }
  }
  const ownerHasNoDeal = ownerCheck ? ownerCheck.state === 'not_applicable' : (summary?.openDealCount ?? 0) === 0
  async function moveClose(check: SalesAccountHealthCheck) {
    const { dealId, suggestedClose } = check.params
    if (!dealId || !suggestedClose) return
    try {
      await updateDeal(dealId, { expectedClose: `${suggestedClose}T00:00:00Z` })
      toast.success(`Close date moved to ${shortDay(suggestedClose, today)}`)
      setNonce(n => n + 1)
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to move the close date'))
    }
  }

  function openLog(opener: HTMLElement | null) {
    logActivity?.open({}, () => opener)
  }

  // The KPI strip's lines (spec §2.3), every figure the server's.
  const openSub = summary && summary.openDealCount > 0
    ? [
        `${compactMoney(summary.openWeightedPence, 'GBP')} weighted`,
        summary.openDealCount === 1 && view.openDeals[0]?.effectiveProbability !== undefined
          ? `${laneLabel(view.openDeals[0].stage)} ${view.openDeals[0].effectiveProbability}%`
          : `${summary.openDealCount} open deals`,
        summary.openValueExcluded > 0 ? `${summary.openValueExcluded} unconverted excluded` : null,
      ].filter(Boolean).join(' · ')
    : 'No open deals'
  const nextClose = summary?.nextClose
  const closesSub = nextClose
    ? [closeRelative(nextClose.expectedClose, today), summary && summary.openDealCount > 1 ? nextClose.dealName : null].filter(Boolean).join(' · ')
    : null
  const wonSub = summary
    ? [summary.wonDealCount === 0 ? 'No won deals' : summary.wonDealCount === 1 ? '1 won deal' : `${summary.wonDealCount} won deals`,
       summary.wonValueExcluded > 0 ? `${summary.wonValueExcluded} unconverted excluded` : null].filter(Boolean).join(' · ')
    : null

  const inputCls = 'w-full h-9 px-3 text-12 bg-surface-control border border-edge-control rounded-control text-ink-body placeholder:text-ink-muted focus:outline-none'

  return (
    <div className="page-enter flex flex-col gap-4">
      {/* Header: breadcrumb and the stepper (spec §2.1) */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-12 text-ink-secondary min-w-0">
          <span>Sales</span>
          <span aria-hidden="true" className="text-ink-faint">›</span>
          <button type="button" onClick={onBack} aria-label="Back to accounts"
            className="font-medium hover:text-ink-primary transition-colors">Accounts</button>
          <span aria-hidden="true" className="text-ink-faint">›</span>
          <span className="text-ink-primary font-medium truncate" aria-current="page">{account.name}</span>
        </nav>
        {onStep && position && (
          <div className="flex items-center gap-2 text-12 text-ink-muted">
            <span>Account {position.index + 1} of {position.total} · by open value</span>
            <button type="button" className="k-btn-secondary px-2 disabled:opacity-40" aria-label="Previous account (K)"
              disabled={!position.prev} onClick={() => position.prev && onStep(position.prev)}><ChevronLeft size={14} /></button>
            <button type="button" className="k-btn-secondary px-2 disabled:opacity-40" aria-label="Next account (J)"
              disabled={!position.next} onClick={() => position.next && onStep(position.next)}><ChevronRight size={14} /></button>
          </div>
        )}
      </div>

      {/* Hero — read view, or the inline edit form in its place */}
      {editing ? (
        <div className="flex-1 min-w-0 max-w-2xl flex flex-col gap-3">
          <input value={draft.name} onChange={e => setDraft(d => ({ ...d, name: e.target.value }))}
            placeholder="Account name" aria-label="Account name" autoFocus={focus === 'name'}
            className={`${inputCls} text-14 font-semibold`} />
          <div className="grid grid-cols-2 gap-3">
            <input value={draft.website} onChange={e => setDraft(d => ({ ...d, website: e.target.value }))}
              placeholder="Website" aria-label="Website" autoFocus={focus === 'website'} className={inputCls} />
            <input value={draft.phone} onChange={e => setDraft(d => ({ ...d, phone: e.target.value }))}
              placeholder="Phone — the main switchboard" aria-label="Phone" autoFocus={focus === 'phone'} className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <AccountCodeSelect kind="region" value={draft.region} onChange={v => setDraft(d => ({ ...d, region: v }))}
              className={inputCls} autoFocus={focus === 'region'} />
            <AccountCodeSelect kind="sector" value={draft.sector} onChange={v => setDraft(d => ({ ...d, sector: v }))}
              className={inputCls} autoFocus={focus === 'sector'} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input value={draft.headOffice} onChange={e => setDraft(d => ({ ...d, headOffice: e.target.value }))}
              placeholder="Head office" aria-label="Head office" maxLength={200} autoFocus={focus === 'headOffice'} className={inputCls} />
            <input value={draft.headcount} onChange={e => setDraft(d => ({ ...d, headcount: e.target.value }))}
              placeholder="Size — number of people" aria-label="Size (number of people)" inputMode="numeric"
              autoFocus={focus === 'headcount'} className={inputCls} />
          </div>
          {/* AD2: billing details (spec §10). Nothing reads them into an invoice yet (§45 ruling 6). */}
          <fieldset className="flex flex-col gap-3">
            <legend className="text-11 font-semibold text-ink-muted mb-1">Billing details</legend>
            <div className="grid grid-cols-2 gap-3">
              <input value={draft.legalEntity} onChange={e => setDraft(d => ({ ...d, legalEntity: e.target.value }))}
                placeholder="Legal entity" aria-label="Legal entity" maxLength={200} autoFocus={focus === 'billing'} className={inputCls} />
              <input value={draft.vatNumber} onChange={e => setDraft(d => ({ ...d, vatNumber: e.target.value }))}
                placeholder="VAT number" aria-label="VAT number" maxLength={30} className={inputCls} />
            </div>
            <textarea value={draft.billingAddress} onChange={e => setDraft(d => ({ ...d, billingAddress: e.target.value }))}
              rows={3} placeholder="Billing address" aria-label="Billing address" maxLength={500} className={`${inputCls} h-auto py-2 resize-none`} />
          </fieldset>
          <textarea value={draft.notes} onChange={e => setDraft(d => ({ ...d, notes: e.target.value }))}
            rows={3} placeholder="Notes" aria-label="Notes" className={`${inputCls} h-auto py-2 resize-none`} />
          <div className="flex items-center gap-2">
            <button className="k-btn disabled:opacity-50" disabled={saving} onClick={save}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button className="k-btn-secondary" disabled={saving} onClick={() => setEditing(false)}>Cancel</button>
          </div>
        </div>
      ) : (
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0 flex items-start gap-3">
            <Avatar name={account.name} size={40} />
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-section-title font-semibold text-ink-primary leading-tight truncate">{account.name}</h1>
                {summary && <span className="k-badge-outline text-ink-secondary">{relationshipChip(summary)}</span>}
                {summary && <span className="k-badge-outline text-ink-secondary">{contactChip(summary)}</span>}
              </div>
              {/* The meta line (spec §2.2) */}
              <p className="text-13 text-ink-secondary mt-1 flex items-center gap-x-3 gap-y-1 flex-wrap">
                {account.website ? (
                  <span className="inline-flex items-center gap-1.5 min-w-0">
                    <a href={hrefFor(account.website)} target="_blank" rel="noreferrer"
                      className="inline-flex items-center gap-1 hover:underline" style={{ color: accent.DEFAULT }}>
                      <Globe size={12} />{shownDomain}
                    </a>
                    {shownDomain !== account.website.trim() && (
                      <span className="text-12 text-ink-muted truncate" title={account.website}>saved as {account.website.trim()}</span>
                    )}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-ink-muted"><Globe size={12} />No website</span>
                )}
                <CodedField icon={<MapPin size={12} />} name="Region" label={regionLabel(account.region)} onAdd={records ? () => startEdit('region') : undefined} />
                <CodedField icon={<Briefcase size={12} />} name="Sector" label={sectorLabel(account.sector)} onAdd={records ? () => startEdit('sector') : undefined} />
                {summary && (summary.owner ? (
                  <span className="inline-flex items-center gap-1"
                    title={`From the largest open deal, ${summary.owner.dealName}`}>
                    <UserRound size={12} className="text-ink-faint" />Owner {summary.owner.name}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-ink-muted"
                    title={ownerHasNoDeal ? 'No open deal to take an owner from' : 'The largest open deal has no owner'}>
                    <UserRound size={12} />No owner
                  </span>
                ))}
                {account.phone ? (
                  <span className="inline-flex items-center gap-1"><Phone size={12} className="text-ink-faint" />{account.phone}</span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-ink-muted">
                    <Phone size={12} />No phone
                    {records && <button type="button" className="k-accounts-add" onClick={() => startEdit('phone')}>Add switchboard</button>}
                  </span>
                )}
              </p>
              {account.notes && (
                <p className="text-12 text-ink-secondary mt-2 max-w-2xl whitespace-pre-wrap">{account.notes}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {records && (
              <button className="k-btn-secondary inline-flex items-center gap-1" onClick={() => startEdit()}>
                <Pencil size={12} /> Edit
              </button>
            )}
            {records && (
              <button className="k-btn-secondary inline-flex items-center gap-1" onClick={() => setCreating('contact')}>
                <Plus size={12} /> Contact
              </button>
            )}
            {records && (
              <button className="k-btn-secondary inline-flex items-center gap-1" onClick={() => setCreating('deal')}>
                <Plus size={12} /> Deal
              </button>
            )}
            {logActivity && (
              <button className="k-btn" onClick={e => openLog(e.currentTarget)}>Log activity</button>
            )}
          </div>
        </div>
      )}

      {/* KPI strip (spec §2.3) — the server's figures only */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <Kpi label="Open pipeline" value={compactMoney(summary.openValueBasePence, 'GBP')} sub={openSub} />
          <Kpi label="Last touch" value={summary.lastTouchAt ? relAgo(summary.lastTouchAt) : 'Never'} sub={ageLine(ageDays)} />
          <Kpi label="People" value={String(summary.contactCount)} sub={summary.contactCount === 1 ? 'person linked' : 'people linked'} />
          <Kpi label="Closes" value={nextClose ? formatDay(`${closeDay(nextClose.expectedClose)}T00:00:00Z`, 'utc') : '—'}
            sub={nextClose ? closesSub : 'No close date on an open deal'} />
          <Kpi label="Won to date" value={compactMoney(summary.wonValueBasePence, 'GBP')} sub={wonSub} />
        </div>
      )}

      {/* Two columns: Deals / Timeline | People / Company / Invoices */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
        <div className="lg:col-span-2 flex flex-col gap-4">
          {/* AD3: ✦ Account health — the server's six checks (spec §3) */}
          {view.health && (
            <AccountHealthCard health={view.health} accountName={account.name} ageDays={ageDays} today={today} />
          )}

          {/* Deals — open first, closed beneath; every row opens Deal Detail */}
          <div className="k-card">
            <div className="k-card-head">
              <div>
                <div className="k-card-title">Deals</div>
                <div className="k-card-sub">{view.openDeals.length} open · {view.closedDeals.length} closed</div>
              </div>
            </div>
            <div className="px-5 py-2">
              <div className="k-eyebrow uppercase pt-2 pb-1">Open deals</div>
              {view.openDeals.length === 0 && <div className="text-13 text-ink-muted py-3">No open deals.</div>}
              {view.openDeals.map(d => (
                <div key={d.id}>
                  <DealRow deal={d} onOpen={onSelectDeal} />
                  {closeCheck && closeCheck.params.dealId === d.id && (
                    <CloseDateCheck check={closeCheck} today={today} onMove={records ? () => moveClose(closeCheck) : undefined} />
                  )}
                </div>
              ))}
              <div className="k-eyebrow uppercase pt-4 pb-1">Closed deals</div>
              {view.closedDeals.length === 0 && <div className="text-13 text-ink-muted py-3">No closed deals yet.</div>}
              {view.closedDeals.map(d => <DealRow key={d.id} deal={d} onOpen={onSelectDeal} />)}
            </div>
          </div>

          <AccountTimeline key={nonce} accountId={accountId} activities={view.activities} summary={summary}
            ageDays={ageDays} onLogFirst={logActivity ? () => openLog(document.activeElement instanceof HTMLElement ? document.activeElement : null) : undefined} />
        </div>

        <div className="flex flex-col gap-4">
          {/* S6: the merges into this account that can still be undone. */}
          {canMerge && (merges.data ?? []).filter(m => !m.undoneAt).length > 0 && (
            <div className="k-card">
              <div className="k-card-head">
                <div>
                  <div className="k-card-title">Merged into this account</div>
                  <div className="k-card-sub">Each merge can be undone</div>
                </div>
              </div>
              <div className="px-5 py-2">
                {(merges.data ?? []).filter(m => !m.undoneAt).map(m => (
                  <div key={m.id} className="flex items-center justify-between gap-3 py-3 border-b border-edge-row last:border-0">
                    <div className="min-w-0 flex items-start gap-2">
                      <GitMerge size={13} className="text-ink-faint mt-0.5 flex-shrink-0" aria-hidden="true" />
                      <div className="min-w-0">
                        <div className="text-13 font-semibold text-ink-primary truncate">{m.mergedName}</div>
                        <div className="text-11 text-ink-muted">
                          Merged {fmtDate(m.mergedAt)} · {movedPhrase(m.movedContactIds.length, m.movedDealIds.length)} moved
                        </div>
                      </div>
                    </div>
                    <button type="button" className="k-btn-secondary flex-shrink-0 disabled:opacity-50" disabled={undoing !== null}
                      aria-label={`Undo merge of ${m.mergedName}`}
                      onClick={async () => {
                        setUndoing(m.id)
                        await undoMerge(m.id, m.mergedName, () => setNonce(n => n + 1))
                        setUndoing(null)
                      }}>
                      {undoing === m.id ? 'Undoing…' : 'Undo merge'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* People */}
          <div className="k-card">
            <div className="k-card-head">
              <div className="k-card-title">People</div>
              {seats.length > 0 && coverageCheck?.params?.needed !== undefined && (
                <span className="text-12 text-ink-muted" data-testid="people-coverage"
                  title={`People linked to ${coverageCheck.params.dealName ?? 'the largest open deal'}, and the roles its value needs that someone holds`}>
                  {peopleHead(coverageCheck.params.linked ?? 0, seats)}
                </span>
              )}
            </div>
            <div className="px-5 py-2">
              {view.contacts.length === 0 && !waiting.some(l => !l.linkedToAccount) && <div className="text-13 text-ink-muted py-3">No contacts linked.</div>}
              {view.contacts.map(c => {
                const w = waitingParts(waitingOf.get(c.id))
                return (
                  <div key={c.id} className="flex items-center gap-3 py-3 border-b border-edge-row last:border-0">
                    <Avatar name={c.name} size={32} />
                    <div className="min-w-0 flex-1">
                      <div className="text-13 font-semibold text-ink-primary truncate">{c.name}</div>
                      <div className="text-12 text-ink-muted truncate">{[c.title, seatOfContact.get(c.id), c.email].filter(Boolean).join(' · ') || '—'}</div>
                      {w.line && <div className="text-12 text-ink-secondary truncate" data-testid="waiting-lead">{w.line}</div>}
                    </div>
                    {w.action}
                  </div>
                )
              })}
              {waiting.filter(l => !view.contacts.some(c => c.id === l.contactId)).map(l => {
                const w = waitingParts(l)
                return (
                  <div key={l.contactId} className="flex items-center gap-3 py-3 border-b border-edge-row last:border-0">
                    <Avatar name={l.fullName} size={32} />
                    <div className="min-w-0 flex-1">
                      <div className="text-13 font-semibold text-ink-primary truncate">{l.fullName}</div>
                      <div className="text-12 text-ink-muted truncate">{[l.title, l.email, 'on one of this account\'s deals, not linked to it yet'].filter(Boolean).join(' · ')}</div>
                      {w.line && <div className="text-12 text-ink-secondary truncate" data-testid="waiting-lead">{w.line}</div>}
                    </div>
                    {w.action}
                  </div>
                )
              })}
              {seats.filter(x => x.filled && x.contactId && !view.contacts.some(c => c.id === x.contactId)).map(x => (
                <div key={x.seat} data-seat={x.seat} data-filled="true" className="flex items-center gap-3 py-3 border-b border-edge-row last:border-0">
                  <Avatar name={x.contactName ?? ''} size={32} />
                  <div className="min-w-0">
                    <div className="text-13 font-semibold text-ink-primary truncate">{x.contactName}</div>
                    <div className="text-12 text-ink-muted truncate">{SEAT_INFO[x.seat].label}</div>
                  </div>
                </div>
              ))}
              {seats.filter(x => !x.filled).map(x => {
                const info = SEAT_INFO[x.seat]
                const url = seatSearchUrl(x.seat, seatDomain)
                return (
                  <div key={x.seat} data-seat={x.seat} data-filled="false"
                    className="flex items-center gap-3 my-2 px-3 py-2.5 rounded-control border border-dashed border-edge-control">
                    <div className="min-w-0 flex-1">
                      <div className="text-13 font-semibold text-ink-secondary">{info.label}</div>
                      <div className="text-12 text-ink-muted">{seatHint(x.seat, seatDomain)}</div>
                    </div>
                    {url ? (
                      <a className="k-btn-secondary flex-shrink-0" href={url} target="_blank" rel="noopener noreferrer"
                        aria-label={`Find ${info.label.toLowerCase()}: search the web for ${info.searchTitle} ${seatDomain} in a new tab`}>Find</a>
                    ) : (
                      <button type="button" className="k-btn-secondary flex-shrink-0 disabled:opacity-40" disabled
                        title="Add the account's website to search for this person">Find</button>
                    )}
                    {records && (
                      <button type="button" className="k-btn-secondary flex-shrink-0 inline-flex items-center gap-1"
                        aria-label={`Add a contact as ${info.label.toLowerCase()}`} onClick={() => setSeatFor(x)}>
                        <Plus size={12} /> Contact
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          {/* Company facts (spec §9) — the fields the account has today */}
          <div className="k-card">
            <div className="k-card-head">
              <div className="k-card-title">Company</div>
              {records && !chOpen && (
                <button type="button" className="k-accounts-add" onClick={() => setChOpen(true)}>Find on Companies House</button>
              )}
            </div>
            {records && chOpen && (
              <CompaniesHouseCard accountId={accountId} accountName={account.name}
                restore={() => chMemory.current} remember={patch => { chMemory.current = { ...chMemory.current, ...patch } }}
                onApplied={() => setNonce(n => n + 1)} onClose={() => { chMemory.current = emptyCompaniesHouseMemory(); setChOpen(false) }} />
            )}
            <div className="px-5 py-1">
              {/* CH1 (0148): shown only once applied from Companies House. */}
              {account.companyNumber && <FactRow name="Company no." value={account.companyNumber} />}
              {account.companyStatus && (
                <FactRow name="Status" value={statusWithReadTime(account.companyStatus, account.companyStatusReadAt ? fmtDate(account.companyStatusReadAt) : null)} />
              )}
              <FactRow name="Industry" value={sectorCoded?.label ?? (account.sector ? account.sector : '')} secondary={sectorCoded?.code}
                reason="Sorts the account into the sector filters." onAdd={records ? () => startEdit('sector') : undefined} />
              <FactRow name="Region" value={regionCoded?.label ?? (account.region ? account.region : '')} secondary={regionCoded?.code}
                reason="Sorts the account into the region filters." onAdd={records ? () => startEdit('region') : undefined} />
              <FactRow name="Head office" value={account.headOffice ?? ''} reason="Where the company is run from."
                onAdd={records ? () => startEdit('headOffice') : undefined} />
              {/* The stored value's hint is on the meta line, once (AD0). */}
              <FactRow name="Website" value={shownDomain}
                reason="Links the account to its company's domain." onAdd={records ? () => startEdit('website') : undefined} />
              <FactRow name="Phone" value={account.phone} reason="No switchboard number."
                onAdd={records ? () => startEdit('phone') : undefined} />
              <FactRow name="Size" value={account.headcount ? headcountText(account.headcount) : ''}
                reason="Add headcount to compare with similar accounts." onAdd={records ? () => startEdit('headcount') : undefined} />
              <FactRow name="Created" value={account.createdAt ? fmtDate(account.createdAt) : ''} reason="Not recorded." />
            </div>
          </div>

          {/* AD2 — Billing details (spec §10's box). The spec's "Finance gets a
              draft invoice with these company details" is NOT shown: nothing
              reads them into an invoice yet (§45 ruling 6). */}
          <div className="k-card">
            <div className="k-card-head">
              <div className="k-card-title">Billing details</div>
            </div>
            <div className="px-5 py-1">
              {billingMissing && (
                <div className="py-2.5 border-b border-edge-row flex items-start justify-between gap-3">
                  <div className="text-12 text-ink-muted">Legal entity, billing address and VAT number are needed before Finance can invoice.</div>
                  {records && (
                    <button type="button" className="k-accounts-add flex-shrink-0" aria-label="Add billing details"
                      onClick={() => startEdit('billing')}>Add</button>
                  )}
                </div>
              )}
              <FactRow name="Legal entity" value={account.legalEntity ?? ''} />
              <FactRow name="Billing address" value={account.billingAddress ?? ''} multiline />
              <FactRow name="VAT number" value={account.vatNumber ?? ''} />
            </div>
          </div>

          {/* Invoices — the issued invoices behind won deals (C5-3: number +
              tab navigation; no per-invoice deep link exists) */}
          <div className="k-card">
            <div className="k-card-head">
              <div className="k-card-title">Invoices</div>
            </div>
            <div className="px-5 py-2">
              {view.invoices.length === 0 && <div className="text-13 text-ink-muted py-3">No invoices issued.</div>}
              {view.invoices.map(inv => (
                <div key={inv.id} className="flex items-center justify-between gap-3 py-3 border-b border-edge-row last:border-0">
                  <div className="min-w-0">
                    {onNavigate ? (
                      <button type="button" onClick={() => onNavigate('finance.issued-invoices')} title="View in Issued Invoices"
                        className="text-13 font-medium hover:underline" style={{ color: accent.DEFAULT }}>
                        {inv.invoiceNumber}
                      </button>
                    ) : (
                      <div className="text-13 font-medium text-ink-primary">{inv.invoiceNumber}</div>
                    )}
                    <div className="text-11 text-ink-muted mt-0.5">{inv.issueDate ? fmtDate(inv.issueDate) : '—'}</div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-13 font-medium text-ink-primary">{money(inv.total, inv.currency)}</span>
                    <span className="k-badge-outline text-ink-muted">{inv.status}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {creating === 'contact' && (
        <NewContactDrawer initialAccount={{ accountId, company: account.name }}
          onClose={() => setCreating(null)}
          onCreated={() => { setCreating(null); setNonce(n => n + 1) }} />
      )}
      {seatFor && (
        <NewContactDrawer initialAccount={{ accountId, company: account.name }}
          subtitle={`Added to ${coverageCheck?.params?.dealName ?? 'the deal'} as ${SEAT_INFO[seatFor.seat].label}`}
          onClose={() => setSeatFor(null)}
          onCreated={c => { void fillSeat(seatFor, c.id, c.name) }} />
      )}
      {creating === 'deal' && (
        <NewDealDrawer initialStage="New Lead" initialAccount={{ accountId, company: account.name }}
          onClose={() => setCreating(null)}
          onCreated={() => setNonce(n => n + 1)}
          onOpenDeal={id => { setCreating(null); onSelectDeal(id) }} />
      )}
    </div>
  )
}
